<template>
    <Dialog v-model:visible="display" :modal="true" class="verification-dialog" :baseZIndex="106">
        <div class="logo logo-dark" />
        <div class="box" data-cy="pending-verification">
            <div class="box-content">
                <div class="headline">
                    <h2>{{$t('verifyemail.headline-2')}}</h2>
                    <h5 class="justify-content-center">{{ app?.appContext?.config?.globalProperties?.$auth?.user?.value?.email }}</h5>
                </div>
                <div class="verify-text" v-if="!emailVerified">
                    <p>
                        {{$t('verifyemail.copy-1')}}
                        <span class="highlight-text">
                            {{ app?.appContext?.config?.globalProperties?.$auth?.user?.value?.email }}
                        </span>
                        {{$t('verifyemail.copy-2')}}
                        <br />
                        <br />
                        {{$t('verifyemail.copy-3')}}
                    </p>
                    <div class="success-message">
                        <div class="verification-buttons" style="justify-content: space-between;">
                            <Button :label="t('verifyemail.change.verification.mail')" @click="changeEmail"
                                    class="button-secondary" />
                            <Button v-if="!emailSent" :label="t('verifyemail.resend.verification.mail')" @click="resendVerificationEmail"
                                    class="button-primary" />
                        </div>
                        <span v-if="emailSent">{{$t('verifyemail.mail-triggered')}}</span>
                    </div>
                </div>
            </div>
        </div>
    </Dialog>
    <ChangeEmail ref="changeEmailDialog" @emailChanged="emailChanged" />
</template>

<script lang="ts" setup>

    import { defineEmits, defineExpose, getCurrentInstance, ref } from "vue";
    import { createCandidateService } from '@/services/candidateService';
    import { createAuthenticationService } from '@/services/authenticationService';
    import { useToast } from 'primevue/usetoast';
    import { useI18n } from "vue-i18n";
    import { CandidateProfile } from "@/models/CandidateProfile";
    import ChangeEmail from './ChangeEmail.vue'

    const emit = defineEmits(['email-verified', 'error']);
    const toaster = useToast();
    const app = getCurrentInstance();
    const emailSent = ref(false);
    const display = ref(false);
    const candidate = ref<CandidateProfile|null>();
    const { t } = useI18n();
    const changeEmailDialog = ref();

    const resendVerificationEmail = async function () {
        const result = await service.refreshToken();
        if (!result) {
            emailSent.value = false;
            return;
        }
        const candidateService = await createCandidateService(app);
        emailSent.value = await candidateService.triggerVerificationMail();
        if (emailSent.value) {
            toaster.add({
                severity: "success",
                summary: t("verifyemail.resend.mail.toast.success.title"),
                detail: t("verifyemail.resend.mail.toast.success.text"),
                life: 5000,
            });
        }
    }

    const service = createAuthenticationService(app);

    const show = async () => {
        const candidateService = await createCandidateService(app);
        if (!display.value) {
            candidate.value = await candidateService.get();
            display.value = true;
            await checkVerification();
        }
    };

    const close = async () => {
        const user = service.getUser();
        if (user && user.email_verified) {
            emit('email-verified');
            display.value = false;
        }
    }

    const changeEmail = () => {
        changeEmailDialog.value.show();
    }

    const emailChanged = async () => {
        await service.refreshToken();
        await service.updateAuthState();
    }

    const checkVerification = async () => {
        const result = await service.refreshToken();
        if (result == "already_requested")
            return;

        if (!result) {
            emit('error');
            return;
        }
        const user = service.getUser();

        if (user && user.email_verified) {
            await close();
        } else {
            if (display.value)
                setTimeout(checkVerification, 5000);
        }
    };

    defineExpose({
        show
    });
</script>
